import { useEffect, useState, useCallback } from 'react';
import { Clock, ExternalLink, ChevronDown, ChevronUp, Copy } from 'lucide-react';
import { api } from '../../api/client';
import { Can } from '../../lib/rbac';

/**
 * Panel "Compras demoradas / posiblemente perdidas" (2026-08-26).
 * Compras Amazon VIVAS con fecha prometida vencida. El vigilante NO recompra solo
 * (no hay cancelación por API → recomprar a ciegas duplicó 9 compras en 45 días).
 * El usuario decide: Pedir cancelación (manual en Amazon) / Recomprar / Cerrar.
 * Se cierra solo con evidencia: entregada, cancelada/reembolsada, o escaneada en depósito.
 */

interface CaseRow {
  id: number;
  ml_order_id: string;
  purchase_id: number;
  amazon_order_id: string | null;
  amount: string | null;
  bucket: 'not_shipped' | 'in_transit';
  promised_end: string | null;
  amazon_status_text: string | null;
  status: 'open' | 'cancel_requested' | 'closed';
  rebuy_purchase_id: number | null;
  detected_at: string;
  asin: string | null;
  title: string;
  amazon_status: string;
  tracking_number: string | null;
  days_overdue: number;
  other_purchase_id: number | null;
  other_purchase_status: string | null;
  other_amazon_order_id: string | null;
  buyer_promised: string | null;
}

interface CasesData {
  total: number;
  counts: Record<string, number>;
  cases: CaseRow[];
}

const BUCKET_LABEL: Record<string, string> = {
  not_shipped: 'Amazon todavía no la despachó',
  in_transit: 'En camino sin novedades',
};

const BUCKET_STYLE: Record<string, string> = {
  not_shipped: 'bg-amber-100 text-amber-800',
  in_transit: 'bg-orange-100 text-orange-800',
};

function fmtDate(d: string | null): string {
  return d ? new Date(d).toLocaleDateString() : '—';
}

export function DelayedCasesPanel({ sellerId }: { sellerId: number }) {
  const [data, setData] = useState<CasesData | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [msg, setMsg] = useState<{ id: number; text: string; ok: boolean } | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get<CasesData>(`/amazon/delayed-cases?seller_id=${sellerId}`);
      if (res.success && res.data) setData(res.data);
      else setData(null);
    } catch {
      setData(null); // panel informativo: sin toast
    }
  }, [sellerId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data || data.total === 0) return null;

  const act = async (c: CaseRow, action: 'cancel-requested' | 'rebuy' | 'close-manual') => {
    if (action === 'rebuy') {
      const warn = c.other_purchase_id
        ? 'Esta venta YA tiene otra compra viva. ¿Recomprar igual?'
        : c.status !== 'cancel_requested'
          ? 'Todavía no pediste la cancelación de la compra original: si Amazon la manda igual, vas a pagar dos veces. ¿Recomprar igual?'
          : '¿Recomprar ahora en Amazon?';
      if (!window.confirm(warn)) return;
    }
    let reason: string | null = null;
    if (action === 'close-manual') {
      reason = window.prompt('Motivo del cierre (queda anotado en el caso):');
      if (!reason || !reason.trim()) return;
    }
    setBusy(c.id);
    setMsg(null);
    try {
      const res = await api.post<{ purchase_id?: number | null }>(
        `/amazon/delayed-cases/${c.id}/${action}`,
        reason ? { seller_id: sellerId, reason } : { seller_id: sellerId }
      );
      if (res.success) {
        setMsg({
          id: c.id,
          ok: true,
          text:
            action === 'rebuy'
              ? `Recompra enviada${res.data?.purchase_id ? ` (compra ${res.data.purchase_id})` : ''}`
              : action === 'cancel-requested'
                ? 'Marcada: cancelación pedida en Amazon'
                : 'Caso cerrado',
        });
      } else {
        setMsg({ id: c.id, ok: false, text: res.error || 'No se pudo' });
      }
    } catch (e) {
      setMsg({ id: c.id, ok: false, text: (e as Error).message });
    } finally {
      setBusy(null);
      load();
    }
  };

  return (
    <div className="rounded-lg border border-amber-500 bg-amber-50 dark:bg-amber-950/30">
      <button
        className="flex w-full flex-wrap items-center gap-2 px-4 py-3 text-left"
        onClick={() => setOpen(!open)}
      >
        <Clock className="h-5 w-5 text-amber-600" />
        <span className="font-semibold text-amber-800 dark:text-amber-300">
          Compras demoradas / posiblemente perdidas: {data.total}
        </span>
        <span className="text-sm text-[var(--color-text-secondary)]">
          {Object.entries(data.counts)
            .map(([b, n]) => `${BUCKET_LABEL[b] || b}: ${n}`)
            .join(' · ')}
        </span>
        <span className="ml-auto text-[var(--color-text-secondary)]">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>
      {open && (
        <div className="max-h-[32rem] overflow-y-auto border-t border-[var(--color-border)] bg-[var(--color-surface)]">
          <p className="px-4 py-2 text-xs text-[var(--color-text-secondary)]">
            El sistema no recompra solo estos casos. Si la compra todavía no salió de Amazon, pedí la
            cancelación desde la web de Amazon y marcala acá; si Amazon la acepta, recomprá. Si el
            paquete llega igual, el caso se cierra solo al escanearlo en el depósito.
          </p>
          {data.cases.map((c) => (
            <div
              key={c.id}
              className="border-b border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-text)]"
            >
              <div className="flex flex-wrap items-center gap-3">
                <span
                  className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${BUCKET_STYLE[c.bucket] || ''}`}
                >
                  {BUCKET_LABEL[c.bucket] || c.bucket}
                </span>
                <span className="min-w-0 flex-1 truncate" title={c.title}>
                  {c.title}
                </span>
                <span className="hidden font-mono text-xs text-[var(--color-text-secondary)] sm:inline">
                  #{c.ml_order_id}
                </span>
                <Can
                  cap="global.view_money"
                  fallback={<span className="shrink-0 text-[var(--color-text-muted)]">—</span>}
                >
                  <span className="shrink-0 font-semibold">
                    {c.amount != null ? `$${Number(c.amount).toFixed(2)}` : '—'}
                  </span>
                </Can>
                <span className="shrink-0 rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                  {c.days_overdue} {c.days_overdue === 1 ? 'día' : 'días'} de atraso
                </span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--color-text-secondary)]">
                <span>Amazon prometió: {fmtDate(c.promised_end)}</span>
                {c.buyer_promised && <span>Al comprador: {fmtDate(c.buyer_promised)}</span>}
                {c.tracking_number && <span>Seguimiento: {c.tracking_number}</span>}
                {c.amazon_status_text && <span>Amazon dice: “{c.amazon_status_text}”</span>}
                {c.other_purchase_id && (
                  <span className="font-medium text-violet-700">
                    Ya hay otra compra viva para esta venta ({c.other_amazon_order_id || c.other_purchase_id}
                    , {c.other_purchase_status})
                  </span>
                )}
                {c.status === 'cancel_requested' && (
                  <span className="font-medium text-amber-700">Cancelación pedida — esperando a Amazon</span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {c.amazon_order_id && (
                  <>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(c.amazon_order_id || '')}
                      className="flex shrink-0 items-center gap-1 rounded border border-[var(--color-border)] px-2 py-1 font-mono text-xs hover:bg-[var(--color-surface-hover)]"
                      title="Copiar número de compra"
                    >
                      <Copy className="h-3 w-3" /> {c.amazon_order_id}
                    </button>
                    <a
                      href={`https://www.amazon.com/gp/your-account/order-details?orderID=${c.amazon_order_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex shrink-0 items-center gap-1 rounded bg-amber-500 px-2 py-1 text-xs font-medium text-white hover:bg-amber-600"
                    >
                      <ExternalLink className="h-3 w-3" /> Abrir en Amazon
                    </a>
                  </>
                )}
                {c.status === 'open' && (
                  <Can cap="orders.cancel_purchase">
                    <button
                      disabled={busy === c.id}
                      onClick={() => act(c, 'cancel-requested')}
                      className="shrink-0 rounded border border-[var(--color-border)] px-2 py-1 text-xs hover:bg-[var(--color-surface-hover)] disabled:opacity-50"
                    >
                      Pedí cancelación
                    </button>
                  </Can>
                )}
                <Can cap="orders.buy">
                  <button
                    disabled={busy === c.id}
                    onClick={() => act(c, 'rebuy')}
                    className="shrink-0 rounded bg-[var(--color-primary)] px-2 py-1 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
                  >
                    {c.rebuy_purchase_id ? 'Recomprar otra vez' : 'Recomprar'}
                  </button>
                </Can>
                <Can cap="orders.cancel_purchase">
                  <button
                    disabled={busy === c.id}
                    onClick={() => act(c, 'close-manual')}
                    className="shrink-0 rounded border border-[var(--color-border)] px-2 py-1 text-xs hover:bg-[var(--color-surface-hover)] disabled:opacity-50"
                  >
                    Cerrar a mano
                  </button>
                </Can>
                {c.rebuy_purchase_id && (
                  <span className="text-xs text-[var(--color-text-secondary)]">
                    Recomprada (compra {c.rebuy_purchase_id})
                  </span>
                )}
                {msg && msg.id === c.id && (
                  <span className={`text-xs ${msg.ok ? 'text-green-700' : 'text-red-700'}`}>
                    {msg.text}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
